import { Close, Content, Description, Overlay, Portal, Root, Title, Trigger } from '@radix-ui/react-dialog';
import clsx from 'clsx';
import type { ComponentProps, FunctionComponent, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

export type DialogProps = {
    readonly children: ReactNode;
    readonly className?: string;
    readonly defaultOpen?: boolean;
    readonly modal?: boolean;
    readonly onOpenChange?: (open: boolean) => void;
    readonly open?: boolean;
};

export type DialogTriggerProps = {
    readonly asChild?: boolean;
    readonly children: ReactNode;
    readonly className?: string;
};

export type DialogContentProps = {
    readonly children: ReactNode;
    readonly className?: string;
    readonly onEscapeKeyDown?: (event: KeyboardEvent) => void;
    readonly onInteractOutside?: (event: Event) => void;
    readonly onOpenAutoFocus?: (event: Event) => void;
    readonly onPointerDownOutside?: (event: Event) => void;
};

export type DialogTitleProps = {
    readonly children: ReactNode;
    readonly className?: string;
};

export type DialogDescriptionProps = {
    readonly children: ReactNode;
    readonly className?: string;
};

export type DialogCloseProps = {
    readonly asChild?: boolean;
    readonly children: ReactNode;
    readonly className?: string;
};

const StyledOverlay: FunctionComponent<ComponentProps<typeof Overlay>> = ({ className, ...props }) => (
    <Overlay className={clsx('fixed inset-0 bg-gray-900 opacity-10 z-[1000]', className)} {...props} />
);

const StyledContent: FunctionComponent<ComponentProps<typeof Content>> = ({ className, ...props }) => (
    <Content
        className={twMerge(
            'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'bg-gray-100 rounded-xxxl shadow-md font-sans',
            'max-h-[85vh] max-w-[90vw] w-[90vw]',
            'px-xl py-l z-[1110]',
            'focus:outline-none',
            'data-[state=open]:animate-dialog-in',
            'data-[state=closed]:animate-dialog-out',
            className,
        )}
        {...props}
    />
);

const StyledTitle: FunctionComponent<ComponentProps<typeof Title>> = ({ className, ...props }) => (
    <Title className={clsx('text-base font-medium leading-[1.5rem] text-black-900 m-0 mb-m', className)} {...props} />
);

const StyledDescription: FunctionComponent<ComponentProps<typeof Description>> = ({ className, ...props }) => (
    <Description
        className={clsx('text-sm font-normal leading-[1.25rem] text-gray-900 m-0 mb-xl', className)}
        {...props}
    />
);

const StyledClose: FunctionComponent<ComponentProps<typeof Close>> = ({ className, ...props }) => (
    <Close
        className={clsx(
            'flex items-center justify-center absolute right-m top-m',
            'h-[2rem] w-[2rem] bg-transparent border-none',
            'text-gray-500 cursor-pointer',
            'focus:outline-none',
            'hover:text-gray-700',
            className,
        )}
        {...props}
    />
);

const Backdrop: FunctionComponent<ComponentProps<'div'>> = ({ className, ...props }) => (
    <div
        className={clsx(
            'fixed inset-0 z-[1101]',
            'bg-white/10 backdrop-blur-[1px]',
            'transition-[background-color] duration-300 ease-in-out',
            className,
        )}
        style={{ WebkitBackdropFilter: 'blur(1px)' }}
        {...props}
    />
);

const DarkBackdrop: FunctionComponent<ComponentProps<'div'>> = ({ className, ...props }) => (
    <div
        className={clsx(
            'fixed inset-0 z-[1101]',
            'bg-black/30 backdrop-blur-[4px]',
            'transition-[background-color] duration-300 ease-in-out',
            className,
        )}
        style={{ WebkitBackdropFilter: 'blur(4px)' }}
        {...props}
    />
);

export const Dialog: FunctionComponent<DialogProps> & {
    Backdrop: typeof Backdrop;
    Close: typeof StyledClose;
    Content: typeof StyledContent;
    DarkBackdrop: typeof DarkBackdrop;
    Description: typeof StyledDescription;
    Overlay: typeof StyledOverlay;
    Portal: typeof Portal;
    Root: typeof Root;
    Title: typeof StyledTitle;
    Trigger: typeof Trigger;
} = ({ children, ...props }) => {
    return <Root {...props}>{children}</Root>;
};

Dialog.Root = Root;
Dialog.Trigger = Trigger;
Dialog.Portal = Portal;
Dialog.Overlay = StyledOverlay;
Dialog.Content = StyledContent;
Dialog.Title = StyledTitle;
Dialog.Description = StyledDescription;
Dialog.Close = StyledClose;
Dialog.Backdrop = Backdrop;
Dialog.DarkBackdrop = DarkBackdrop;
